import React, { useState, useEffect, useCallback } from 'react';
import { Download, BarChart3, PieChart as PieChartIcon, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { todayCentral } from '../../shared/utils/dateUtils';

const SchemeReports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState('collection');
  const [dateRange, setDateRange] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Report data
  const [paymentReport, setPaymentReport] = useState(null);
  const [redemptionReport, setRedemptionReport] = useState(null);
  const [redemptionReportSummary, setRedemptionReportSummary] = useState(null);
  const [schemeAnalytics, setSchemeAnalytics] = useState(null);
  const [customerAnalytics, setCustomerAnalytics] = useState(null);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load collection report
      const collectionResponse = await fetch(
        `/api/schemes/payments/report/collection?fromDate=${startDate}&toDate=${endDate}`
      );
      const collectionData = await collectionResponse.json();
      if (collectionData.success) {
        setPaymentReport(collectionData.data);
      }

      // Load redemption report
      const redemptionResponse = await fetch(
        `/api/schemes/redemptions/report?fromDate=${startDate}&toDate=${endDate}`
      );
      const redemptionData = await redemptionResponse.json();
      if (redemptionData.success) {
        setRedemptionReport(redemptionData.data);
        setRedemptionReportSummary(redemptionData.summary);
      }

      // Load scheme analytics
      const schemeResponse = await fetch('/api/schemes/analytics/summary');
      const schemeData = await schemeResponse.json();
      if (schemeData.success) {
        setSchemeAnalytics(schemeData.data);
      }

      // Load customer analytics
      const customerResponse = await fetch(`/api/schemes/analytics/customer?fromDate=${startDate}&toDate=${endDate}`);
      const customerData = await customerResponse.json();
      if (customerData.success) {
        setCustomerAnalytics(customerData.data);
      }
    } catch (err) {
      setError('Error loading reports: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]); // Removed reportType dependency

  // Set default dates on mount
  useEffect(() => {
    const today = new Date();
    const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(monthAgo.toISOString().split('T')[0]);
  }, []);

  // Load reports when start/end date changes
  useEffect(() => {
    if (startDate && endDate) {
      loadReports();
    }
  }, [startDate, endDate, loadReports]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    let start;

    switch (range) {
      case 'week':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        break;
      case 'quarter':
        start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        break;
      case 'year':
        start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        break;
      default:
        start = new Date();
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const exportToCSV = (data, filename) => {
    if (!data) return;

    // Convert data to CSV
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape quotes in values
          return typeof value === 'string' && value.includes(',')
            ? `"${value}"`
            : value;
        }).join(',')
      )
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${todayCentral()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto text-amber-500 mb-4" size={32} />
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.REPORTS} pageName="Reports" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Scheme Reports & Analytics</h1>
        <p className="text-gray-500 mt-1">Payment collection, redemption, and customer analytics</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow-lg p-4 border border-amber-100 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-2">
            {['week', 'month', 'quarter', 'year'].map(range => (
              <button
                key={range}
                onClick={() => handleDateRangeChange(range)}
                className={`px-4 py-2 rounded-lg font-medium transition ${dateRange === range
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex gap-2 flex-1">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <button
              onClick={loadReports}
              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
            >
              <RefreshCw size={18} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {schemeAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

          {/* Total Schemes */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
            <p className="text-gray-600 text-sm mb-1 font-medium">Total Schemes</p>
            <p className="text-3xl font-bold text-blue-600">{schemeAnalytics.schemes?.total || 0}</p>
            <div className="mt-3 flex gap-2">
              <span className="flex-1 text-center bg-amber-100 text-amber-800 text-xs font-bold py-1 rounded">
                {schemeAnalytics.schemes?.inr || 0} INR
              </span>
              <span className="flex-1 text-center bg-blue-200 text-blue-900 text-xs font-bold py-1 rounded">
                {schemeAnalytics.schemes?.usd || 0} USD
              </span>
            </div>
          </div>

          {/* Active Enrollments */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
            <p className="text-gray-600 text-sm mb-1 font-medium">Active Enrollments</p>
            <p className="text-3xl font-bold text-green-600">{schemeAnalytics.enrollments?.active || 0}</p>
            <div className="mt-3 flex gap-2">
              <span className="flex-1 text-center bg-amber-100 text-amber-800 text-xs font-bold py-1 rounded">
                {schemeAnalytics.enrollments?.inr?.active || 0} INR
              </span>
              <span className="flex-1 text-center bg-blue-100 text-blue-900 text-xs font-bold py-1 rounded">
                {schemeAnalytics.enrollments?.usd?.active || 0} USD
              </span>
            </div>
          </div>

          {/* Collection by Currency */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
            <p className="text-gray-600 text-sm mb-2 font-medium">Collection by Currency</p>
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <p className="text-xs text-amber-600 font-bold uppercase mb-0.5">INR Schemes</p>
                <div className="flex justify-between items-baseline">
                  <span className="text-lg font-bold text-amber-700">
                    ₹{parseFloat(schemeAnalytics.financials?.inr?.totalPaid || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </span>
                  <span className="text-xs text-red-500 font-medium">
                    ₹{parseFloat(schemeAnalytics.financials?.inr?.totalPending || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })} due
                  </span>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <p className="text-xs text-blue-600 font-bold uppercase mb-0.5">USD Schemes</p>
                <div className="flex justify-between items-baseline">
                  <span className="text-lg font-bold text-blue-700">
                    ${parseFloat(schemeAnalytics.financials?.usd?.totalPaid || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                  <span className="text-xs text-red-500 font-medium">
                    ${parseFloat(schemeAnalytics.financials?.usd?.totalPending || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} due
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Completed Enrollments */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-5 border border-amber-200">
            <p className="text-gray-600 text-sm mb-1 font-medium">Completed Enrollments</p>
            <p className="text-3xl font-bold text-amber-600">{schemeAnalytics.enrollments?.completed || 0}</p>
            <div className="mt-3 flex gap-2">
              <span className="flex-1 text-center bg-amber-200 text-amber-900 text-xs font-bold py-1 rounded">
                {schemeAnalytics.enrollments?.inr?.completed || 0} INR
              </span>
              <span className="flex-1 text-center bg-blue-100 text-blue-900 text-xs font-bold py-1 rounded">
                {schemeAnalytics.enrollments?.usd?.completed || 0} USD
              </span>
            </div>
          </div>

        </div>
      )}

      {/* Report Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          {[
            { id: 'collection', label: 'Payment Collection', icon: BarChart3 },
            { id: 'redemption', label: 'Redemption Analysis', icon: TrendingUp },
            { id: 'customerwise', label: 'Customer-wise', icon: PieChartIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setReportType(tab.id)}
              className={`pb-3 px-4 font-medium border-b-2 transition flex items-center gap-2 ${reportType === tab.id
                  ? 'text-amber-600 border-amber-500'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
                }`}
            >
              <tab.icon size={18} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Payment Collection Report */}
      {reportType === 'collection' && paymentReport && (
        <div className="bg-white rounded-lg shadow-lg border border-amber-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 flex justify-between items-center">
            <h3 className="text-white font-bold text-lg">Payment Collection Report</h3>
            <button
              onClick={() => exportToCSV(paymentReport || [], 'payment-collection')}
              className="bg-white hover:bg-gray-100 text-blue-600 px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>

          {/* Summary Stats */}
          {schemeAnalytics?.financials && (
            <div className="p-6 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-3">INR Schemes</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Amount Due</p>
                    <p className="text-lg font-bold text-gray-800">₹{parseFloat(schemeAnalytics.financials.inr?.totalDue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Collected</p>
                    <p className="text-lg font-bold text-green-700">₹{parseFloat(schemeAnalytics.financials.inr?.totalPaid || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="text-lg font-bold text-red-600">₹{parseFloat(schemeAnalytics.financials.inr?.totalPending || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-3">USD Schemes</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Amount Due</p>
                    <p className="text-lg font-bold text-gray-800">${parseFloat(schemeAnalytics.financials.usd?.totalDue || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Collected</p>
                    <p className="text-lg font-bold text-green-700">${parseFloat(schemeAnalytics.financials.usd?.totalPaid || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending</p>
                    <p className="text-lg font-bold text-red-600">${parseFloat(schemeAnalytics.financials.usd?.totalPending || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-100 border-b-2 border-amber-300">
                  <th className="px-6 py-3 text-left text-xs font-bold text-amber-950 uppercase tracking-wide">Scheme Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-amber-950 uppercase tracking-wide">Customer Name</th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-amber-950 uppercase tracking-wide">Month</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-amber-950 uppercase tracking-wide">Due</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-amber-950 uppercase tracking-wide">Collected</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-amber-950 uppercase tracking-wide">Pending</th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-amber-950 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paymentReport?.length > 0 ? (() => {
                  const usdRows = paymentReport.filter(p => p.currency === 'USD').slice(0, 50);
                  const inrRows = paymentReport.filter(p => p.currency !== 'USD').slice(0, 50);
                  const rows = [];

                  if (usdRows.length > 0) {
                    rows.push(
                      <tr key="usd-section">
                        <td colSpan="7" className="px-6 py-2 bg-blue-100 text-blue-900 text-xs font-bold uppercase tracking-wider">
                          $ USD Schemes
                        </td>
                      </tr>
                    );
                    usdRows.forEach((payment, idx) => rows.push(
                      <tr key={`usd-${idx}`} className="bg-white hover:bg-blue-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-800">{payment.scheme_name}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{payment.customer_name}</td>
                        <td className="px-6 py-3 text-sm text-center text-gray-600">Month {payment.payment_month}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-gray-800 text-right">${parseFloat(payment.total_due || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-green-600 text-right">${parseFloat(payment.total_collected || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-red-600 text-right">${parseFloat(payment.total_pending || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${payment.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {payment.is_paid ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ));
                  }

                  if (inrRows.length > 0) {
                    rows.push(
                      <tr key="inr-section">
                        <td colSpan="7" className="px-6 py-2 bg-amber-100 text-amber-900 text-xs font-bold uppercase tracking-wider">
                          ₹ INR Schemes
                        </td>
                      </tr>
                    );
                    inrRows.forEach((payment, idx) => rows.push(
                      <tr key={`inr-${idx}`} className="bg-white hover:bg-amber-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-800">{payment.scheme_name}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{payment.customer_name}</td>
                        <td className="px-6 py-3 text-sm text-center text-gray-600">Month {payment.payment_month}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-gray-800 text-right">₹{parseFloat(payment.total_due || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-green-600 text-right">₹{parseFloat(payment.total_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm font-semibold text-red-600 text-right">₹{parseFloat(payment.total_pending || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-6 py-3 text-sm text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${payment.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {payment.is_paid ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ));
                  }

                  return rows;
                })() : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-600">No payment data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {paymentReport?.length > 50 && (
            <div className="p-4 text-center text-gray-600 text-sm bg-gray-50">
              Showing 50 of {paymentReport.length} payments
            </div>
          )}
        </div>
      )}

      {/* Redemption Report */}
      {reportType === 'redemption' && redemptionReport && (
        <div className="bg-white rounded-lg shadow-lg border border-amber-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 flex justify-between items-center">
            <h3 className="text-white font-bold text-lg">Redemption Analysis</h3>
            <button
              onClick={() => exportToCSV(redemptionReport || [], 'redemption-analysis')}
              className="bg-white hover:bg-gray-100 text-green-600 px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>

          {/* Summary Stats */}
          {redemptionReportSummary && (
            <div className="p-6 border-b border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-600 text-sm">Total Redemptions</p>
                <p className="text-2xl font-bold text-gray-800">{redemptionReportSummary.total_redemptions || 0}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm">Total Value</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{parseFloat(redemptionReportSummary.total_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-sm">Avg Redemption</p>
                <p className="text-2xl font-bold text-blue-600">
                  ₹{parseFloat(redemptionReportSummary.average_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          )}

          {/* Redemptions Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Customer</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Scheme</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Redeemed Via</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800">Amount</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.isArray(redemptionReport) && redemptionReport.slice(0, 20).map((redemption, idx) => (
                  <tr key={idx} className={`${redemption.currency === 'USD'
                      ? 'hover:bg-blue-50 ' + (idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30')
                      : 'hover:bg-amber-50 ' + (idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30')
                    }`}>
                    <td className="px-6 py-3 text-sm">{new Date(redemption.redeemed_date).toLocaleDateString()}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-800">{redemption.customer_name}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      <span className="flex items-center gap-2">
                        {redemption.scheme_name}
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${redemption.currency === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                          {redemption.currency === 'USD' ? '$ USD' : '₹ INR'}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                        {redemption.redeemed_via || 'jewelry'}
                      </span>
                    </td>
                    <td className={`px-6 py-3 text-sm font-semibold text-right ${redemption.currency === 'USD' ? 'text-blue-600' : 'text-amber-600'}`}>
                      {redemption.currency === 'USD' ? '$' : '₹'}{parseFloat(redemption.redeemed_amount || 0).toLocaleString(redemption.currency === 'USD' ? 'en-US' : 'en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">{redemption.notes || '—'}</td>
                  </tr>
                ))}
                {(!Array.isArray(redemptionReport) || redemptionReport.length === 0) && (
                  <tr><td colSpan="6" className="px-6 py-4 text-center text-gray-500">No redemptions in this date range</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {Array.isArray(redemptionReport) && redemptionReport.length > 20 && (
            <div className="p-4 text-center text-gray-600 text-sm bg-gray-50">
              Showing 20 of {redemptionReport.length} redemptions
            </div>
          )}
        </div>
      )}

      {/* Customer-wise Analysis */}
      {reportType === 'customerwise' && customerAnalytics && (
        <div className="bg-white rounded-lg shadow-lg border border-amber-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 flex justify-between items-center">
            <h3 className="text-white font-bold text-lg">Customer-wise Analysis</h3>
            <button
              onClick={() => exportToCSV(Array.isArray(customerAnalytics) ? customerAnalytics.flatMap(c => c.enrollments.map(e => ({ customer: c.customerName, ...e }))) : [], 'customer-analytics')}
              className="bg-white hover:bg-gray-100 text-purple-600 px-4 py-2 rounded-lg font-medium transition flex items-center gap-2"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>

          {/* Customers Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wide">Scheme</th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wide">Monthly</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wide">Total</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wide">Paid</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wide">Pending</th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wide">%</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(customerAnalytics) && customerAnalytics.length > 0 ? (
                  customerAnalytics.map((customer, cidx) => (
                    (customer.enrollments || []).map((enr, eidx) => {
                      const isUSD = enr.currency === 'USD';
                      const sym = isUSD ? '$' : '₹';
                      const locale = isUSD ? 'en-US' : 'en-IN';
                      const isFirstRow = eidx === 0;
                      const isLastRow = eidx === (customer.enrollments || []).length - 1;
                      const pct = parseFloat(enr.paymentPercentage || 0);

                      return (
                        <tr
                          key={`${cidx}-${eidx}`}
                          className={`border-b ${isLastRow ? 'border-gray-300' : 'border-gray-100'} hover:bg-gray-50`}
                        >
                          {/* Customer name — only on first enrollment row, spans all */}
                          <td className={`px-6 py-3 text-sm font-semibold text-gray-800 align-top ${!isFirstRow ? 'text-transparent select-none' : ''}`}>
                            {isFirstRow ? (
                              <span>
                                {customer.customerName}
                                {(customer.enrollments || []).length > 1 && (
                                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded-full">
                                    {(customer.enrollments || []).length}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-200 text-xs pl-2 border-l-2 border-gray-200">↳</span>
                            )}
                          </td>

                          {/* Scheme + currency badge */}
                          <td className="px-6 py-3 text-sm text-gray-700">
                            <span className="flex items-center gap-2">
                              {enr.schemeName}
                              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${isUSD ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                {isUSD ? '$ USD' : '₹ INR'}
                              </span>
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-3 text-sm text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${enr.status === 'active' ? 'bg-green-100 text-green-800'
                                : enr.status === 'completed' ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                              {enr.status}
                            </span>
                          </td>

                          {/* Monthly */}
                          <td className="px-6 py-3 text-sm text-right text-gray-600">
                            {sym}{parseFloat(enr.monthlyAmount).toLocaleString(locale, { maximumFractionDigits: 0 })}
                            <span className="text-xs text-gray-400 ml-1">×{enr.durationMonths}mo</span>
                          </td>

                          {/* Total */}
                          <td className={`px-6 py-3 text-sm font-semibold text-right ${isUSD ? 'text-blue-700' : 'text-amber-700'}`}>
                            {sym}{parseFloat(enr.totalAmount).toLocaleString(locale, { maximumFractionDigits: 0 })}
                          </td>

                          {/* Paid */}
                          <td className="px-6 py-3 text-sm font-semibold text-right text-green-600">
                            {sym}{parseFloat(enr.paidAmount).toLocaleString(locale, { maximumFractionDigits: 0 })}
                          </td>

                          {/* Pending */}
                          <td className="px-6 py-3 text-sm font-semibold text-right text-orange-600">
                            {sym}{parseFloat(enr.pendingAmount).toLocaleString(locale, { maximumFractionDigits: 0 })}
                          </td>

                          {/* % */}
                          <td className="px-6 py-3 text-sm text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${pct >= 80 ? 'bg-green-100 text-green-800'
                                : pct >= 50 ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                              {enr.paymentPercentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-600">No customer data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {Array.isArray(customerAnalytics) && customerAnalytics.length > 20 && (
            <div className="p-4 text-center text-gray-600 text-sm bg-gray-50">
              Showing {customerAnalytics.length} customers
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchemeReports;
